import type { SentenceChunk } from "./types";

const enc = new TextEncoder();

/**
 * Split input text into sentence-sized chunks for pipelined TTS synthesis.
 * Splits on sentence-ending punctuation (. ? ;) followed by whitespace or
 * end-of-string, or on newlines. The delimiter stays with the preceding chunk.
 * `!` and `,` are intentionally NOT split points (they fragment speech too
 * aggressively and hurt prosody). Returns byte offsets (UTF-8) for each chunk.
 */
export function chunkBySentence(text: string): SentenceChunk[] {
  if (text.length === 0) return [];

  // Match sentence-ending punctuation followed by whitespace, or newlines.
  // The split point is after the punctuation + trailing whitespace, or after \n.
  // We use a regex that captures the delimiter so we can keep it with the preceding chunk.
  // Pattern: ([.?;][ \t]+) — punctuation followed by at least one space/tab
  //        | ([.?;]$)      — punctuation at end of string
  //        | (\n)          — newline
  const parts: string[] = [];
  const re = /([.?;][ \t]+|[.?;]\n|[.?;]$|\n)/g;

  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = re.exec(text)) !== null) {
    const end = match.index + match[0].length;
    parts.push(text.slice(lastIndex, end));
    lastIndex = end;
  }

  // Remaining text after last match
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  if (parts.length === 0) {
    return [{ text, startOffset: 0 }];
  }

  const chunks: SentenceChunk[] = [];
  let byteOffset = 0;

  for (const part of parts) {
    if (part.length > 0) {
      chunks.push({ text: part, startOffset: byteOffset });
      byteOffset += enc.encode(part).length;
    }
  }

  return chunks;
}
